<template>
  <div
    v-if="showHeader"
    class="mb-2 flex justify-between text-base font-bold md:text-sm"
  >
    {{ heading }}
  </div>

  <div class="relative isolate z-1">
    <button
      class="relative w-full cursor-pointer rounded-lg border"
      @click="isOpen = !isOpen"
    >
      <div class="flex items-center justify-between gap-2 p-4">
        <div v-if="activeOption" class="flex w-full justify-between">
          <slot name="selected-option" :option="activeOption">
            <span>{{ activeOption.label }}</span>
          </slot>
        </div>
        <span v-else>{{ placeholder || heading }}</span>
        <IconChevron
          :direction="isOpen ? 'up' : 'down'"
          class="z-10 shrink-0 text-2xl text-black"
        />
      </div>
    </button>

    <template v-if="!isMobile && isOpen">
      <div
        class="fixed inset-0 h-screen w-screen bg-transparent"
        @click="onClickOutsideOnDesktop"
      ></div>
      <div :class="classNames[design]">
        <span
          v-if="design === 'attached'"
          class="block p-4 text-black"
          @click="isOpen = false"
          >{{ heading }}</span
        >
        <div class="max-h-[325px] overflow-y-auto rounded-lg bg-white">
          <slot name="options" :on-option-click="onOptionClick">
            <DropdownSelectorList
              class="overflow-hidden rounded-b-lg"
              :options="options"
              :active-option="activeOption"
              @click="onOptionClick"
            >
              <template #optionItem="{ option }">
                <slot name="optionItem" :option="option"></slot>
              </template>
            </DropdownSelectorList>
          </slot>
        </div>
      </div>
    </template>
  </div>

  <Drawer v-if="isMobile" v-model:active="isOpen">
    <template #header>
      <h1 class="text-lg font-bold">
        {{ heading }}
      </h1>
    </template>

    <slot name="options" :on-option-click="onOptionClick">
      <DropdownSelectorList
        :options="options"
        :active-option="activeOption"
        @click="onOptionClick"
      />
    </slot>
  </Drawer>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';

import { useViewport } from '@/shared/composables/useViewport';

import Drawer from './Drawer.vue';
import DropdownSelectorList from './DropdownSelectorList.vue';
import IconChevron from './Icons/IconChevron.vue';
import { OptionType } from './ui.types';

const props = withDefaults(
  defineProps<{
    heading?: string;
    options?: OptionType[];
    active: OptionType | null;
    placeholder?: string;
    showHeader?: boolean;
    design?: 'detached' | 'attached';
    open?: boolean;
  }>(),
  {
    heading: '',
    options: () => [],
    placeholder: '',
    showHeader: true,
    design: 'attached',
    open: false
  }
);

const isOpen = ref(props.open);
const activeOption = ref<OptionType | null>(props.active);

const { isMobile } = useViewport();

const classNames = {
  detached:
    'absolute top-full left-0 bg-white shadow-md rounded-lg w-auto z-10 border mt-1 bg-white',
  attached:
    'absolute top-0 left-0 z-product-dropdown-content w-full rounded-lg border bg-white shadow-thin'
};

watch(isMobile, (newVal) => {
  if (!newVal) {
    isOpen.value = false;
  }
});

watch(
  () => props.active,
  (newVal) => {
    activeOption.value = newVal;
  }
);

// Watch external open prop and sync to internal state
watch(
  () => props.open,
  (newVal) => {
    isOpen.value = newVal;
  }
);

// Watch internal state and emit to parent
watch(isOpen, (newVal) => {
  emit('update:open', newVal);
});

const onClickOutsideOnDesktop = () => {
  isOpen.value = false;
};

const emit = defineEmits(['option-click', 'update:open']);

const onOptionClick = (option: OptionType) => {
  activeOption.value = option;
  isOpen.value = false;
  emit('option-click', option);
};
</script>
